import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { motion } from 'framer-motion';
import AnimatedNumber from './AnimatedNumber';
import LoadingSkeleton from './LoadingSkeleton';
import { RADIUS } from '../../theme/tokens';

// Tone map resolved at render time via theme to avoid hardcoding hex
function buildToneMap(theme) {
  return {
    primary: { bg: theme.palette.primary.soft ?? 'rgba(242,101,34,0.12)', iconColor: theme.palette.primary.main },
    accent: { bg: 'rgba(247,179,43,0.12)', iconColor: theme.palette.accent?.main ?? theme.palette.warning.main },
    navy: { bg: theme.palette.secondary.main, iconColor: '#FFFFFF' },
    success: { bg: 'rgba(22,163,74,0.1)', iconColor: theme.palette.success.main },
    coin: { bg: 'rgba(255,215,0,0.12)', iconColor: theme.palette.accent?.main ?? theme.palette.warning.main },
  };
}

const MotionBox = motion(Box);


/**
 * StatCard
 * Displays a single metric with icon, label, value, optional helper text, and trend.
 *
 * @param {ReactNode} icon
 * @param {string} label
 * @param {number|string} value
 * @param {string} [helper]
 * @param {{ delta: string|number, direction: 'up'|'down' }} [trend]
 * @param {'primary'|'accent'|'navy'|'success'|'coin'} [tone='primary']
 * @param {() => void} [onClick]
 * @param {boolean} [loading]
 */
function StatCard({
  icon,
  label,
  value,
  helper,
  trend,
  tone = 'primary',
  onClick,
  loading = false,
  sx = {},
}) {
  const theme = useTheme();
  const toneMap = buildToneMap(theme);
  const colors = toneMap[tone] ?? toneMap.primary;

  if (loading) {
    return <LoadingSkeleton variant="stat" />;
  }

  return (
    <MotionBox
      whileHover={{ y: -2, boxShadow: theme.shadowsExt?.md }}
      whileTap={onClick ? { scale: 0.97 } : undefined}
      onClick={onClick}
      sx={{
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: RADIUS.md,
        p: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s',
        boxShadow: theme.shadowsExt?.sm,
        height: '100%',
        ...sx,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: RADIUS.md,
          bgcolor: colors.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
          color: colors.iconColor,
          '& .MuiSvgIcon-root': { fontSize: theme.iconSizes?.lg ?? 24 },
        }}
      >
        {icon}
      </Box>

      {/* Value */}
      <Typography variant="h4" fontWeight={700} color="text.primary" lineHeight={1.1}>
        {typeof value === 'number' ? (
          <AnimatedNumber value={value} />
        ) : (
          value
        )}
      </Typography>

      {/* Label */}
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        {label}
      </Typography>

      {/* Helper + trend */}
      {(helper || trend) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          {trend && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: trend.direction === 'up' ? 'success.main' : 'error.main',
              }}
            >
              {trend.direction === 'up' ? (
                <TrendingUp sx={{ fontSize: 14 }} />
              ) : (
                <TrendingDown sx={{ fontSize: 14 }} />
              )}
              <Typography variant="caption" fontWeight={600} ml={0.25}>
                {trend.delta}
              </Typography>
            </Box>
          )}
          {helper && (
            <Typography variant="caption" color="text.tertiary">
              {helper}
            </Typography>
          )}
        </Box>
      )}
    </MotionBox>
  );
}

export default StatCard;
